/* eslint-disable react/require-default-props */
import React from 'react';
import Loader from 'components/atoms/loader';
import AiManagementDataCard from 'components/molecules/ai_management_data_card';
import { usePossibleCompetitorContracts } from 'hooks/usePossibleCompetitorContracts';
import { PossibleCompetitorContract } from 'services/actions/ai_management_possible_competitors';
import { dateBr, dateTimeBr, money } from 'services/ai_management_format';
import { Content } from './styles';

interface AiManagementCompetitorContractsProps {
  biddingId: number | null;
  historicBiddingId: number;
  cnpj: string;
  className?: string;
}

const contractLabel = (contract: PossibleCompetitorContract) =>
  [contract.numero ? `Contrato nº ${contract.numero}` : 'Contrato', money(contract.valor)]
    .filter(Boolean)
    .join(' • ');

const validity = (contract: PossibleCompetitorContract) =>
  contract.vigencia_inicio || contract.vigencia_fim
    ? `Vigência ${dateBr(contract.vigencia_inicio)} a ${dateBr(contract.vigencia_fim)}`
    : null;

const AiManagementCompetitorContracts: React.FC<AiManagementCompetitorContractsProps> = ({
  biddingId,
  historicBiddingId,
  cnpj,
  className = '',
}) => {
  const { contracts, loading, error } = usePossibleCompetitorContracts(
    biddingId,
    historicBiddingId,
    cnpj,
  );

  const rows: React.ReactNode[] = [];
  contracts.forEach((contract) => {
    if (contract.arquivos.length) {
      contract.arquivos.forEach((file) => {
        rows.push(
          <tr key={`file-${file.id ?? file.url}`}>
            <td>
              <div className="file-title">{file.titulo}</div>
              <small className="text-muted">
                {[file.tipo, contractLabel(contract)].filter(Boolean).join(' • ')}
              </small>
            </td>
            <td className="text-nowrap">{dateTimeBr(file.data)}</td>
            <td className="text-right text-nowrap">
              <a href={file.url} target="_blank" rel="noreferrer">
                Download
              </a>
            </td>
          </tr>,
        );
      });
      return;
    }
    rows.push(
      <tr key={`contract-${contract.id ?? contractLabel(contract)}`}>
        <td>
          <div className="file-title">{contractLabel(contract)}</div>
          <small className="text-muted">
            {[validity(contract), 'Sem arquivo disponível'].filter(Boolean).join(' • ')}
          </small>
        </td>
        <td className="text-nowrap">{dateBr(contract.assinatura)}</td>
        <td className="text-right text-muted">—</td>
      </tr>,
    );
  });

  const renderBody = () => {
    if (loading) {
      return (
        <div className="py-3">
          <Loader />
        </div>
      );
    }
    if (error) {
      return (
        <div className="px-3 py-3 text-muted">
          Não foi possível carregar os contratos desta licitação.
        </div>
      );
    }
    if (!rows.length) {
      return (
        <div className="px-3 py-3 text-muted">
          Nenhum contrato desta empresa encontrado para esta licitação.
        </div>
      );
    }
    return (
      <Content className="table-responsive">
        <table className="table bg-white mb-0">
          <thead className="thead-light">
            <tr>
              <th scope="col">Nome do arquivo</th>
              <th scope="col" className="text-nowrap">
                Data
              </th>
              <th scope="col">
                <span className="sr-only">Download</span>
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </Content>
    );
  };

  return (
    <AiManagementDataCard compact className={className}>
      {renderBody()}
    </AiManagementDataCard>
  );
};

export default AiManagementCompetitorContracts;
